import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  allowanceRateQuestion,
  basicInfoQuestions,
  personalAllowancesWorksheetQuestionsPt2,
  personalAllowanceWorksheetQuestions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFNameMiddleInitial = () => {
    const fName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name || '';
    return fName + middleInitial;
  };
  prefilledData.employee_first_name_middle_initial = formatFNameMiddleInitial();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const allowanceRateForm = useRef();
  const allowanceRateInitQ = getInitialQuestions(allowanceRateQuestion);
  const [allowanceRate, setAllowanceRate] = useState(allowanceRateInitQ);

  const personalAllowanceMultipleSelectInitQ = getInitialQuestions(personalAllowanceWorksheetQuestions);
  const [personalAllowanceMultSelect, setPersonalAllowanceMultSelect] = useState(personalAllowanceMultipleSelectInitQ);

  const allowanceForm = useRef();
  const personalAllowancesPt2InitQ = getInitialQuestions(personalAllowancesWorksheetQuestionsPt2);
  const [personalAllowancesPt2, setPersonalAllowancesPt2] = useState(personalAllowancesPt2InitQ);

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <Text style={{ fontWeight: '600', fontSize: '14px' }} mb="10px">
          Exemption from withholding
        </Text>
        <S.QuestionLabel mb="7px" isRequired>
          To qualify for exempt status you must verify with the Kansas Department of Revenue that: 1) last year you
          had the right to a refund of all STATE income tax withheld because you had no tax liability; and 2) this
          year you will receive a full refund of all STATE income tax withheld because you will have no tax liability.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              I claim exemption from withholding.
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              I do not claim exemption from withholding
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowancesForm = (isReviewStep) => (
    <Flex column>
      <Text style={{ fontWeight: '600', fontSize: '14px' }} mb="10px">
        Personal Allowances
      </Text>
      <FormConsumer
        questions={allowanceRate}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowanceRate.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowanceRate(questions);
        }}
        ref={allowanceRateForm}
      />
      <FormConsumer
        questions={personalAllowanceMultSelect}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = personalAllowanceMultSelect.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setPersonalAllowanceMultSelect(questions);
        }}
      />
      <FormConsumer
        questions={personalAllowancesPt2}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = personalAllowancesPt2.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setPersonalAllowancesPt2(questions);
        }}
        ref={allowanceForm}
      />
    </Flex>
  );

  const calculateTotalNumAllowances = () => {
    let numAllowances = 0;
    const personalAllowancesSelected = personalAllowanceMultSelect[0].answer.answer.personal_allowances;

    if (personalAllowancesSelected.includes('cannot_be_claimed_as_dependent')) numAllowances += 1;
    if (personalAllowancesSelected.includes('married_one_job')) numAllowances += 1;
    if (personalAllowancesSelected.includes('head_of_household')) numAllowances += 2;

    const numDependents = parseInt(personalAllowancesPt2[0].answer.answer.dependents) || 0;
    numAllowances += numDependents;

    return numAllowances;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderAllowancesForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowanceRateForm && allowanceRateForm.current) {
              allowanceRateForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowanceForm && allowanceForm.current) {
              allowanceForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step

              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const exemptionStatus = { is_exempt: isExemptFromWithholding };

              let allowanceRateData;
              let personalAllowanceMultSelectData;
              let personalAllowancesPt2Data;

              let totalNumAllowancesTemp = '';

              if (!isExemptFromWithholding) {
                allowanceRateData = allowanceRate.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                personalAllowanceMultSelectData = personalAllowanceMultSelect.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                personalAllowancesPt2Data = personalAllowancesPt2.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                totalNumAllowancesTemp = calculateTotalNumAllowances();
              } else {
                allowanceRateData = resetInitialQuestions(allowanceRateQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                personalAllowanceMultSelectData = resetInitialQuestions(personalAllowanceWorksheetQuestions).reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                personalAllowancesPt2Data = resetInitialQuestions(personalAllowancesWorksheetQuestionsPt2).reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
              }

              const calculatedAllowanceData = { total_num_allowances: totalNumAllowancesTemp };
              const allAllowancesData = {
                ...allowanceRateData,
                ...personalAllowanceMultSelectData,
                ...personalAllowancesPt2Data,
                ...calculatedAllowanceData,
              };
              const data = { ...basicInfoData, ...exemptionStatus, ...allAllowancesData };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderAllowancesForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LegacyForm;
